import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  TextField,
  Stack,
} from '@mui/material'
import { Security, VpnKey } from '@mui/icons-material'
import { useChangePassword } from '../../hooks/useAuth'

export default function SecuritySettingsPage() {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Security /> {t('security.title')}
      </Typography>

      <ChangePasswordCard />
    </Box>
  )
}

function ChangePasswordCard() {
  const { t } = useTranslation()
  const changePassword = useChangePassword()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const submit = async () => {
    setError('')
    setSuccess(false)
    if (newPassword.length < 8) {
      setError(t('security.passwordTooShort'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('security.passwordMismatch'))
      return
    }
    try {
      await changePassword.mutateAsync({ current_password: currentPassword, new_password: newPassword })
      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (e: any) {
      setError(
        e?.response?.status === 401
          ? t('security.wrongCurrentPassword')
          : t('security.passwordChangeFailed'),
      )
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <VpnKey /> {t('security.changePassword')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('security.changePasswordSubtitle')}
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{t('security.passwordChanged')}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <TextField
            type="password"
            label={t('security.currentPassword')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <TextField
            type="password"
            label={t('security.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <TextField
            type="password"
            label={t('security.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <Box>
            <Button
              variant="contained"
              onClick={submit}
              disabled={
                changePassword.isPending ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {t('security.save')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
